import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { VemigoDateRangeField } from 'src/components/VemigoGrid/VemigoDateRangeField';
import SalesPartnerService from 'src/services/salesPartner/salesPartnerServices';
import { SalesPartnerPaymentServices } from 'src/services/salesPartner/salesPartnerPaymentServices';
import { useSalesPartnerData } from '../../SalesPartnerData';

interface Payment {
  id: string;
  date: string;
  amount: number;
  paymentMode: number;
  voucherNumber: string;
}

interface SettlementData {
  openingBalance: number;
  grossEarnings: number;
  payments: Payment[];
  totalPayment: number;
  netEarnings: number;
  finalBalance: number;
  openingBalanceDate?: string;
  selectedDateRange?: string[];
}

const Settlements = () => {
  const { salesPartner } = useSalesPartnerData();

  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  const [displayDates, setDisplayDates] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [settlementData, setSettlementData] = useState<SettlementData>({
    openingBalance: 0,
    grossEarnings: 0,
    payments: [],
    totalPayment: 0,
    netEarnings: 0,
    finalBalance: 0,
    openingBalanceDate: '',
    selectedDateRange: [],
  });
  const [dataLoaded, setDataLoaded] = useState(false);


  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
    }).format(value ?? 0);

  const parseDate = (dateStr: string) => {
    if (!dateStr) return new Date();
    const [d, m, y] = dateStr.split('/');
    return new Date(`${m}/${d}/${y}`);
  };

  const formatDate = (date: Date, opts?: Intl.DateTimeFormatOptions) =>
    date.toLocaleDateString('en-IN', opts || { day: '2-digit', month: 'short', year: 'numeric' });

  const convertToBackendFormat = (dateStr: string) => {
    if (!dateStr) return '';
    const [d, m, y] = dateStr.split('/');
    return `${y}/${m}/${d}`;
  };

  const getCurrentMonthRange = useCallback((): string[] => {
    const now = new Date();
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const format = (d: Date) =>
      `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
    return [format(first), format(last)];
  }, []);

  // Check if date range represents a full month
  const isFullMonthRange = (dates: string[]) => {
    if (dates.length !== 2) return false;
    const [start, end] = dates;
    const startDate = parseDate(start);
    const endDate = parseDate(end);

    // Check if start is first day of month and end is last day of month
    const isStartFirst = startDate.getDate() === 1;
    const isEndLast = endDate.getDate() === new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0).getDate();
    const sameMonthYear = startDate.getMonth() === endDate.getMonth() &&
      startDate.getFullYear() === endDate.getFullYear();

    return isStartFirst && isEndLast && sameMonthYear;
  };

  const getMonthName = (dateStr: string) => {
    const date = parseDate(dateStr);
    return date.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  };

  const fetchSettlementData = async () => {
    if (!salesPartner?.id || selectedDates.length !== 2) return;
    setLoading(true);

    try {
      const [start, end] = selectedDates;
      const formattedStart = convertToBackendFormat(start);
      const formattedEnd = convertToBackendFormat(end);

      // 1. Opening Balance
      const balanceRes = await SalesPartnerService.getSalesPartnerSettlementBalance(
        salesPartner.id,
        formattedStart
      );

      let openingBal = balanceRes?.balance ?? 0;
      const balanceDate = balanceRes?.balanceOnDate;

      // 2. Partial Earnings
      if (balanceDate && new Date(balanceDate) < parseDate(start)) {
        const prevDay = new Date(parseDate(start));
        prevDay.setDate(prevDay.getDate() - 1);
        const prevDayStr = convertToBackendFormat(
          `${String(prevDay.getDate()).padStart(2, '0')}/${String(prevDay.getMonth() + 1).padStart(2, '0')}/${prevDay.getFullYear()}`
        );

        const partialData = await SalesPartnerService.earningData(
          salesPartner.id,
          balanceDate,
          prevDayStr
        );

        const partialEarnings = partialData?.totalEarning ?? 0;
        openingBal += partialEarnings;
      }

      // 3. Gross Earnings
      const earningData = await SalesPartnerService.earningData(
        salesPartner.id,
        formattedStart,
        formattedEnd
      );
      const grossEarnings = earningData?.totalEarning ?? 0;

      // 4. Payments
      const payments = await SalesPartnerPaymentServices.getPaymentsBySalesPartnerIdAndDateRange(
        salesPartner.id,
        formattedStart,
        formattedEnd
      );
      const totalPayment = payments.reduce(
        (sum: number, p: Payment) => sum + (p.amount ?? 0),
        0
      );

      // 5. Calculations
      const netEarnings = openingBal + grossEarnings;
      const finalBalance = netEarnings - totalPayment;

      setSettlementData({
        openingBalance: openingBal,
        grossEarnings,
        payments,
        totalPayment,
        netEarnings,
        finalBalance,
        openingBalanceDate: balanceDate,
        selectedDateRange: selectedDates,
      });
      setDisplayDates(selectedDates);
      setDataLoaded(true);
      enqueueSnackbar('Settlement data loaded successfully', { variant: 'success' });
    } catch (error) {
      console.error('SETTLEMENT ERROR:', error);
      enqueueSnackbar('Error fetching settlement data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    const currentMonthRange = getCurrentMonthRange();
    setSelectedDates(currentMonthRange);
    setDisplayDates(currentMonthRange);
  }, [getCurrentMonthRange]);

  const getEarningsLabel = () => {
    if (displayDates.length !== 2) return 'Gross Earnings';

    if (isFullMonthRange(displayDates)) {
      return `Gross Earnings - 
      ${getMonthName(displayDates[0])}
      `;
    }

    const [start, end] = displayDates;
    return `Gross Earnings 
    (${start} - ${end})
    `;
  };

  const getOpeningBalanceDate = () => {
    if (settlementData.openingBalanceDate) {
      const date = new Date(settlementData.openingBalanceDate);
      return formatDate(date);
    }
    return '';
  };

  const handleDateSelection = (dates: string[]) => {
    setSelectedDates(dates);
    if (dates.length === 2) {
      setShowDatePicker(false);
    }
  };

  return (
    <Container maxWidth="xl">
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Settlements Overview
      </Typography>

      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <VemigoDateRangeField
              width="250px"
              selectedSEDates={selectedDates}
              setSelectedSEDates={handleDateSelection}
              show={showDatePicker}
              setShow={setShowDatePicker}
              mode="range"
            />
            <Button
              variant="contained"
              disabled={loading || !salesPartner || selectedDates.length !== 2}
              onClick={fetchSettlementData}
              sx={{ minHeight: '56px', px: 4, fontWeight: 'bold' }}
            >
              {loading ? <CircularProgress size={24} /> : 'Apply'}
            </Button>
          </Box>
        </Grid>
      </Grid>

      {loading ? (
        <Box sx={{ textAlign: 'center', my: 4 }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Loading settlement data...</Typography>
        </Box>
      ) : !dataLoaded ? (
        null
      ) : (
        <>
          {/* --- Summary Cards --- */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  borderLeft: `4px solid ${settlementData.finalBalance >= 0 ? '#4CAF50' : '#F44336'
                    }`,
                  background: settlementData.finalBalance >= 0 ?
                    'linear-gradient(135deg, #e8f5e8 0%, #ffffff 100%)' :
                    'linear-gradient(135deg, #ffebee 0%, #ffffff 100%)',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" color="text.secondary">
                    Final Balance
                  </Typography>
                  <Typography
                    variant="h4"
                    fontWeight="bold"
                    color={
                      settlementData.finalBalance >= 0
                        ? 'success.main'
                        : 'error.main'
                    }
                  >
                    {formatCurrency(settlementData.finalBalance)}
                  </Typography>
                  <Chip
                    label={
                      settlementData.finalBalance >= 0
                        ? 'Amount Due'
                        : 'Overpaid'
                    }
                    color={
                      settlementData.finalBalance >= 0 ? 'success' : 'error'
                    }
                    size="small"
                    sx={{ mt: 1 }}
                  />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{
                background: 'linear-gradient(135deg, #e3f2fd 0%, #ffffff 100%)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" color="text.secondary">
                    Net Earnings
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="primary">
                    {formatCurrency(settlementData.netEarnings)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{
                background: 'linear-gradient(135deg, #fff3e0 0%, #ffffff 100%)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" color="text.secondary">
                    {getEarningsLabel()}
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="secondary">
                    {formatCurrency(settlementData.grossEarnings)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{
                background: 'linear-gradient(135deg, #f3e5f5 0%, #ffffff 100%)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" color="text.secondary">
                    Opening Balance
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="#9C27B0">
                    {formatCurrency(settlementData.openingBalance)}
                  </Typography>
                  {getOpeningBalanceDate() && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      as of {getOpeningBalanceDate()}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Settlement Breakdown
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell>Opening Balance</TableCell>
                          <TableCell align="right">
                            {formatCurrency(settlementData.openingBalance)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>{getEarningsLabel()}</TableCell>
                          <TableCell align="right" style={{ color: '#4CAF50' }}>
                            +{formatCurrency(settlementData.grossEarnings)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Deductions (if any)</TableCell>
                          <TableCell align="right" style={{ color: '#f44336' }}>
                            -{formatCurrency(0)}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: 'grey.50' }}>
                          <TableCell>
                            <b>Net Earnings</b>
                          </TableCell>
                          <TableCell align="right">
                            <b style={{ color: '#2196F3' }}>{formatCurrency(settlementData.netEarnings)}</b>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Total Payments</TableCell>
                          <TableCell align="right" style={{ color: '#f44336' }}>
                            -{formatCurrency(settlementData.totalPayment)}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{
                          backgroundColor: settlementData.finalBalance >= 0 ? '#e8f5e8' : '#ffebee',
                        }}>
                          <TableCell>
                            <b>Final Balance</b>
                          </TableCell>
                          <TableCell align="right">
                            <b style={{
                              color: settlementData.finalBalance >= 0 ? '#4CAF50' : '#f44336'
                            }}>
                              {formatCurrency(settlementData.finalBalance)}
                            </b>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              {settlementData.payments.length > 0 ? (
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      Payment History ({settlementData.payments.length} payments)
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableBody>
                          {settlementData.payments.map((payment, index) => (
                            <TableRow key={payment.id || index}>
                              <TableCell>
                                {formatDate(new Date(payment.date))}
                                <br />
                                <Typography variant="caption" color="text.secondary">
                                  {payment.paymentMode === 0 ? 'Online' : 'Cash'} • {payment.voucherNumber}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography fontWeight="bold">
                                  {formatCurrency(payment.amount)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              ) : (
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      Payment History
                    </Typography>
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="text.secondary">
                        No payments in selected period
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              )}
            </Grid>
          </Grid>
        </>
      )}
    </Container>
  );
};

export default Settlements;